import _ from "lodash";
import { Form, getIn } from "formik";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import * as Yup from "yup";

import { adminRequests } from "@/api/(requests)/admin";
import Button from "@/components/UI/Button";
import { Button2 } from "@/ui2/atoms/Button";
import CardHeading from "@/components/UI/Headings/CardHeading";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import InputError from "@/components/UI/InputError";
import Label from "@/components/UI/Label";

export function ProviderPracticeDetails(props) {
  const [providerDetails, setProviderDetails] = useState(props.providerDetails);

  const [editProviderPracticeInfo, setEditProviderPracticeInfo] = useState(false);
  const [allPracticeOptions, setAllPracticeOptions] = useState();
  const [practicesById, setPracticesById] = useState({});

  // filtered additional practices is all practices minus the default practice
  const [filteredAdditionalPracticeIds, setFilteredAdditionalPracticeIds] = useState([]);
  const [additionalPracticesInputDefault, setAdditionalPracticesInputDefault] = useState(null);

  useEffect(() => {
    (async () => {
      const response = await adminRequests.fetch_all_practices({
        context: { showLoader: false },
        searchParams: { fields: "ids" },
      });
      const allPracticeOptions = response.map((practice) => {
        return {
          label: practice["name"],
          value: practice["id"],
        };
      });

      setAllPracticeOptions(allPracticeOptions);
      const practicesById = response.reduce((obj, item) => {
        obj[item["id"]] = item;

        return obj;
      }, {});

      setPracticesById(practicesById);

      const filteredAdditionalPracticeIds = filterAdditionalPractices(providerDetails.practice_ids);

      setFilteredAdditionalPracticeIds(filteredAdditionalPracticeIds);

      setAdditionalPracticesInputDefault(
        getAdditionalPracticesInputDefault(filteredAdditionalPracticeIds, practicesById),
      );
    })();
  }, []);

  const defaultPracticeInput = (formikProps) => {
    const inputName = "default_practice_id";
    const error = getIn(formikProps.errors, inputName);
    const touched = getIn(formikProps.touched, inputName);

    return (
      <>
        <Label>Default Practice</Label>
        <Select
          className="basic-multi-select"
          classNamePrefix="select"
          components={{ ClearIndicator: null }}
          defaultValue={{
            label: providerDetails.practice,
            value: providerDetails.practice_id,
          }}
          onChange={async (selectedOptions) =>
            await formikProps.setFieldValue(inputName, selectedOptions)
          }
          options={allPracticeOptions}
        />
        {error && touched ? <InputError classes="custom-error">{error}</InputError> : null}
      </>
    );
  };

  const getAdditionalPracticesInputDefault = (filteredAdditionalPracticeIds, practicesById) => {
    let defaultValue = null;

    if (filteredAdditionalPracticeIds) {
      defaultValue = filteredAdditionalPracticeIds
        .map((id) => {
          const practice = practicesById[id];

          if (!practice) {
            return;
          }

          return {
            label: practice.name,
            value: practice.id,
          };
        })
        .filter((value) => value !== undefined);
    }

    return defaultValue;
  };

  const additionalPracticesInput = (formikProps) => {
    const inputName = "additional_practices";
    const error = getIn(formikProps.errors, inputName);
    const touched = getIn(formikProps.touched, inputName);

    return (
      <>
        <Label>Additional Practices</Label>
        <Select
          className="basic-multi-select"
          classNamePrefix="select"
          components={{ ClearIndicator: null }}
          defaultValue={additionalPracticesInputDefault}
          isMulti
          onChange={async (selectedOptions) =>
            await formikProps.setFieldValue(inputName, selectedOptions)
          }
          options={allPracticeOptions}
        />
        {error && touched ? <InputError classes="custom-error">{error}</InputError> : null}
      </>
    );
  };

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="mx-auto px-0 mb-3">
          <div className="container px-0">
            <div className="row">
              <div className="col-12 col-lg-6">{defaultPracticeInput(formikProps)}</div>
              {providerDetails.provider_type === "CARE_MANAGER" && (
                <div className="col-12 col-lg-6">{additionalPracticesInput(formikProps)}</div>
              )}
            </div>
          </div>
        </div>
        <div className="d-lg-flex d-block text-center justify-content-lg-end tw:justify-center">
          <Button
            className="Btn Btn--otl-pri Btn-sm mr-lg-3 mb-3 mb-lg-0"
            onClick={() => setEditProviderPracticeInfo(!editProviderPracticeInfo)}
            type="button"
          >
            Cancel
          </Button>
          <Button className="Btn Btn--pri Btn-sm" type="submit">
            Save
          </Button>
        </div>
      </Form>
    );
  };

  const onSubmit = (formData) => {
    const defaultPracticeId = _.get(formData, "default_practice_id.value");
    const defaultPracticeName = _.get(formData, "default_practice_id.label");
    const additionalPracticeIds = [defaultPracticeId];

    const additionalPractices = _.get(formData, "additional_practices");

    if (additionalPractices) {
      for (let additionalPractice of additionalPractices) {
        additionalPracticeIds.push(_.get(additionalPractice, "value"));
      }
    }

    const data = {
      additional_practice_ids: additionalPracticeIds,
      default_practice_id: defaultPracticeId,
      practice: defaultPracticeName,
      provider_id: _.get(providerDetails, "username"),
    };

    adminRequests
      .change_provider_practice({ data })
      .then((response) => {
        setEditProviderPracticeInfo(!editProviderPracticeInfo);

        const clonedProviderDetails = { ...providerDetails };

        clonedProviderDetails["practice"] = _.get(response, "practice");
        clonedProviderDetails["practice_id"] = _.get(response, "practice_id");
        clonedProviderDetails["practice_ids"] = additionalPracticeIds;
        setProviderDetails(clonedProviderDetails);

        const filteredAdditionalPracticeIds = filterAdditionalPractices(
          clonedProviderDetails["practice_ids"],
        );

        setFilteredAdditionalPracticeIds(filteredAdditionalPracticeIds);
        setAdditionalPracticesInputDefault(
          getAdditionalPracticesInputDefault(filteredAdditionalPracticeIds, practicesById),
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  if (!providerDetails) {
    return <></>;
  }

  const filterAdditionalPractices = (practiceIds) => {
    return practiceIds.filter((id) => id !== providerDetails.practice_id);
  };

  const additionalPracticeNames = (practiceIds) => {
    const practiceNames = practiceIds
      .map((id) => {
        const practice = practicesById[id];

        if (!practice) {
          return;
        }

        return practice.name;
      })
      .filter((name) => name !== undefined);

    return (
      <>
        {practiceNames.map((name) => (
          <p className="fs-14 mx-0 mx-md-3 mb-4" key={name}>
            {name}
          </p>
        ))}
      </>
    );
  };

  const validation = Yup.object().shape({
    additional_practices: Yup.array().of(Yup.string()).nullable(),
    default_practice_id: Yup.string().required("Default Practice is required."),
  });

  return (
    <DashboardCard2
      Actions={
        <Button2
          onPress={() => setEditProviderPracticeInfo(!editProviderPracticeInfo)}
          variant="link"
        >
          Edit
        </Button2>
      }
      data-public="true"
      title="Practice Details"
    >
      {!editProviderPracticeInfo && (
        <div className="row no-gutters mt-4">
          <div className="col-12 col-lg-6">
            <CardHeading className="fs-14 mx-0 mx-md-3" text="Default Practice" />
            <p className="fs-14 mx-0 mx-md-3 mb-4">{providerDetails.practice}</p>
          </div>

          {providerDetails.provider_type === "CARE_MANAGER" && (
            <div className="col-12 col-lg-6">
              <CardHeading className="fs-14 mx-0 mx-md-3" text="Additional Practices" />
              {additionalPracticeNames(filteredAdditionalPracticeIds)}
            </div>
          )}
        </div>
      )}

      {editProviderPracticeInfo && (
        <CustomForm
          initialValues={{
            additional_practices: additionalPracticesInputDefault,
            default_practice_id: {
              label: _.get(providerDetails, "practice"),
              value: _.get(providerDetails, "practice_id"),
            },
          }}
          onSubmit={onSubmit}
          render={renderForm}
          validationSchema={validation}
        />
      )}
    </DashboardCard2>
  );
}
